import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GitPullRequest, Rocket, Wand2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Polls /api/sync-events/recent every 15s and toasts any new commits landing
 * on main — regardless of source (Emergent Save-to-GitHub via the sync-agent,
 * or VibeKonnect direct commits).
 *
 * Only mounted for super-admins from AppLayout. Silently no-ops if the
 * endpoint 403s or errors.
 */
const SOURCE_META = {
  'emergent-sync': { icon: GitPullRequest, label: 'Emergent → GitHub', tint: 'text-blue-600' },
  'vibekonnect':   { icon: Wand2,           label: 'VibeKonnect',        tint: 'text-violet-600' },
  'auto-merge':    { icon: Rocket,          label: 'Auto-merge',         tint: 'text-emerald-600' },
};

export default function SyncNotifier({ role }) {
  const lastSeenAt = useRef(new Date().toISOString());  // start "now" — no stale spam on load

  useEffect(() => {
    if (role !== 'super_admin') return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/sync-events/recent`, {
          params: { since: lastSeenAt.current },
        });
        if (cancelled || !data.events?.length) return;
        // Toast in chronological order (API returns newest-first — reverse for reading order)
        [...data.events].reverse().forEach(e => {
          const meta = SOURCE_META[e.source] || SOURCE_META['auto-merge'];
          const Icon = meta.icon;
          const shortSha = (e.commit_sha || '').slice(0, 7);
          const prPart = e.pr_number ? ` · PR #${e.pr_number}` : '';
          const extraPart = e.extra ? ` · ${e.extra}` : '';
          toast.success(
            <div className="flex items-start gap-2" data-testid={`sync-toast-${e.id}`}>
              <Icon className={`h-4 w-4 mt-0.5 ${meta.tint}`} />
              <div className="text-xs">
                <div className="font-semibold">{meta.label} — deployed</div>
                <div className="text-gray-500 font-mono mt-0.5">{shortSha}{prPart}{extraPart}</div>
              </div>
            </div>,
            { duration: 6000 },
          );
        });
        // Advance the cursor to the newest event we just showed
        lastSeenAt.current = data.events[0].at;
      } catch { /* silently ignore — endpoint may 403 while auth still boots */ }
    };

    poll();  // fire once on mount
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [role]);

  return null;  // headless
}
